import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { Box } from '@abyss/web/ui/Box';
import { V2Footer } from '@abyss/web/ui/Footer';
import { SubFooterLink } from '@abyss/web/ui/Footer/V2Footer.types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getCoverageTypeString,
  getYearIndex,
  getZipCodeFromLatLong,
} from '../../frontends/GuestExperience/Guest.utils';
import { useConfig } from '../../frontends/ProviderSearch/context/Analytics';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { Constants } from '../Constants';
import { FooterColumn, getFooterData } from './utils';

export const GuestFooter = () => {
  const [section, setSection] = useState<FooterColumn[]>([]);
  const [subFooterLinks, setSubFooterLinks] = useState<SubFooterLink[]>([]);
  const configuration = useLagoon('footer-configuration')();
  const configurationKey = JSON.stringify(configuration);
  const { t } = useTranslation();
  const sectionLabel = t('SECTION');
  const generalLabel = t('GENERAL');
  const obj = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA
  );
  const { planYear = '' } =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.GUEST_USER) || {};
  const [, getFpcGuestToken] = useFpcGuestToken({});
  const { stateCode, zipcode, longitude, latitude } = useGeoLocationStorage();
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';
  const { selectedPlan = {}, coverageType = '' } = obj ? JSON.parse(obj) : {};
  const url = window.location.href;
  const showPrint = url.includes('/guest') && url.includes('/browse');
  const FPC_BASE_URL = config('FPC_BASE_URL');
  const mapKey = useConfig('MAPBOX_KEY');

  const navigateToFpcGuest = async () => {
    const apiUrl = config('API_GATEWAY_URL');
    let urlZipCode = zipcode;
    const language = getLanguage()?.code;
    const yearIndex = getYearIndex(selectedPlan?.years, planYear);
    const yearObj = selectedPlan?.years?.[yearIndex];

    const planIdentifier =
      typeof selectedPlan?.planIdentifier === 'string'
        ? selectedPlan?.planIdentifier.replace(/-/g, '')
        : '';
    if (!urlZipCode) {
      urlZipCode = await getZipCodeFromLatLong(
        apiUrl,
        longitude,
        latitude,
        mapKey
      );
    }
    const token = await getFpcGuestToken({
      variables: {
        planName: selectedPlan?.planName,
        delsys: planIdentifier,
        coverageType: getCoverageTypeString(coverageType),
        language: language || 'en',
        clusterKey: yearObj?.clusterKey || '',
        fipsCode: fipscode,
        partnerId: 'uhc',
      },
    }).then(
      async (response: { data: { getFpcGuestToken: { token: string } } }) =>
        response?.data?.getFpcGuestToken?.token || ''
    );

    const printUrl = `${FPC_BASE_URL}/guest/${token}?planyear=${planYear}&isDirectoryPrint=true&zipCode=${urlZipCode}`;
    window.open(printUrl, '_blank');
  };

  useEffect(() => {
    const footerData: ReturnType<typeof getFooterData> = getFooterData(
      configuration,
      sectionLabel,
      generalLabel
    );
    setSection(footerData?.section);

    const subLinks: SubFooterLink[] = footerData?.general?.[0]?.links.map(
      (link) => ({
        label: link?.name,
        href: link?.url || '#',
      })
    );

    setSubFooterLinks(subLinks);
  }, [configurationKey, sectionLabel, generalLabel]);

  return (
    <Box
      css={{
        'abyss-box-root': {
          backgroundColor: '$white',
          width: '100%',
          opacity: 1,
          fontFamily: '$primary',
          marginTop: 'auto',
          padding: '0px',
        },
      }}
    >
      <Box
        css={{
          'abyss-box-root': {
            width: '100%',
            padding: '24px 48px',
            background: '#FBFCFE',
            alignItems: 'flex-start',
          },
        }}
      >
        <Box
          css={{
            'abyss-box-root': {
              alignSelf: 'stretch',
              background: '#FBFCFE',
              color: '#323333',
              fontSize: 12,
              padding: '0px',
            },
          }}
        >
          <span
            data-testid="footer-timestamp-label"
            style={{ fontWeight: '600' }}
          >
            {t('TIMESTAMP')}
          </span>
          <span
            data-testid="footer-timestamp-text"
            style={{
              marginLeft: 4,
              fontWeight: '400',
            }}
          >
            {t('FOOTER_TIMESTAMP_TEXT')}
          </span>
        </Box>
      </Box>
      <V2Footer
        css={{
          'abyss-footer-copyright': {
            display: 'none',
          },
          'abyss-footer-sub-footnotes': {
            justifyContent: 'flex-start',
            display: 'block',
          },
          'abyss-footer-sub-link': {
            fontSize: '12px',
          },
        }}
        data-testid="guest-footer"
        disableDefaultProviderProps={true}
        fullWidth
        subFooter={{
          links: subFooterLinks,
        }}
      >
        {section?.length > 0 &&
          section.map((column, i) => (
            <V2Footer.Section
              css={{
                'abyss-footer-section-title': {
                  fontSize: '$xl !important',
                },
                'abyss-footer-link-item': {
                  textDecoration: 'none',
                },
              }}
              data-testid="footer-heading"
              key={`footer-column-${i}`}
              title={column?.header}
            >
              {column.links.map((link, j) => (
                <V2Footer.Link
                  css={{
                    'abyss-footer-link': {
                      textDecoration: 'none',
                      display:
                        link.name === 'Print'
                          ? showPrint
                            ? 'flex'
                            : 'none'
                          : 'flex',
                      lineHeight: 1.3,
                    },
                  }}
                  data-testid="footer-links"
                  href={link.url}
                  key={`footer-link-${i}-${j}`}
                  onClick={(e) => {
                    if (link.name === 'Print') {
                      e.preventDefault();
                      navigateToFpcGuest();
                    }
                  }}
                  openNewWindow
                >
                  {link.name}
                </V2Footer.Link>
              ))}
            </V2Footer.Section>
          ))}
      </V2Footer>
    </Box>
  );
};

export default GuestFooter;
